/**
 * @fileoverview Lazy first-page PDF thumbnails for the Library Finder.
 *
 * The Finder's thumbnail tiles call `loadPreviewImageUrl` when a file
 * declares `previewPageCount` but no eager preview URL. This module renders
 * page 1 of the PDF with pdfjs-dist at thumbnail scale and returns a PNG
 * data URL. Results (including failures, as null) are cached per URL in a
 * module-level map so scrolling, view switches, and Finder remounts never
 * re-render the same document. Renders are limited to two at a time so a
 * folder full of PDFs doesn't fan out dozens of concurrent fetch+rasterise
 * jobs when its tiles mount together.
 */

import { pdfjsLib } from "@/utils/pdf/pdfjs";

/** Target bitmap width; tiles render at ~48-224px CSS so 320px stays crisp. */
const THUMBNAIL_MAX_WIDTH = 320;
const MAX_CONCURRENT_RENDERS = 2;

const previewCache = new Map<string, Promise<string | null>>();

// --- Tiny FIFO semaphore -----------------------------------------------

let activeRenders = 0;
const waiters: Array<() => void> = [];

function acquireRenderSlot(): Promise<void> {
  if (activeRenders < MAX_CONCURRENT_RENDERS) {
    activeRenders++;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waiters.push(() => {
      activeRenders++;
      resolve();
    });
  });
}

function releaseRenderSlot(): void {
  activeRenders--;
  waiters.shift()?.();
}

// --- Rendering ----------------------------------------------------------

async function renderFirstPage(url: string): Promise<string | null> {
  await acquireRenderSlot();
  let doc: pdfjsLib.PDFDocumentProxy | undefined;
  try {
    // enableScripting defaults to false, which suppresses embedded
    // OpenAction scripts (see PdfViewer for the full rationale).
    doc = await pdfjsLib.getDocument({ url }).promise;
    const page = await doc.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    if (baseViewport.width <= 0) return null;

    const scale = Math.min(THUMBNAIL_MAX_WIDTH / baseViewport.width, 2);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.ceil(viewport.width));
    canvas.height = Math.max(1, Math.ceil(viewport.height));
    const canvasContext = canvas.getContext("2d");
    if (!canvasContext) return null;

    await page.render({ canvas, canvasContext, viewport }).promise;
    return canvas.toDataURL("image/png");
  } finally {
    releaseRenderSlot();
    void doc?.loadingTask.destroy();
  }
}

/**
 * Render page 1 of the PDF at `url` to a PNG data URL, or null when the
 * document can't be fetched or rasterised. Concurrency-limited and cached
 * by URL (failures cache too, so a broken document is only attempted once
 * per session).
 */
export function loadPdfPreview(url: string): Promise<string | null> {
  const cached = previewCache.get(url);
  if (cached) return cached;

  const pending = renderFirstPage(url).catch(() => null);
  previewCache.set(url, pending);
  return pending;
}
